import React, { useState } from 'react';
import { Loader2, Printer, Download, Brain, List, AlertCircle, X, FileText, Sparkles, RefreshCw, Edit3, Save, XCircle, AlertTriangle } from 'lucide-react';
import AIFormatter from './AIFormatter';
import EditableAIBox from './EditableAIBox';
import { forms as NEW_FORMS } from '../data/forms';

// Rapor UI'ları
import { BasarisizlikReportUI } from './reports/BasarisizlikReports';
import { YasamPenceremReportUI } from './reports/YasamPenceremReports';
import { MeslekiDegerlerReportUI } from './reports/MeslekiDegerlerReports';
import { MeslekiEgilimReportUI } from './reports/MeslekiEgilimReports';
import { DevamsizlikReportUI } from './reports/DevamsizlikReports';
import { KariyerPusulasiReportUI } from './reports/KariyerPusulasiReports';
import { BeckAnksiyeteReportUI } from './reports/BeckAnksiyeteReports';
import { SinavKaygisiReportUI } from './reports/SinavKaygisiReports';
import { InternetBagimliligiReportUI } from './reports/InternetBagimliligiReports';
import { Dass21ReportUI } from './reports/Dass21Reports';
import { RosenbergReportUI } from './reports/RosenbergReports';
import { HollandReportUI } from './reports/HollandReports';
import { VakReportUI } from './reports/VakReports';
import { CokluZekaReportUI } from './reports/CokluZekaReports';
import { AsrsReportUI } from './reports/AsrsReports';
import { SnapIVReportUI } from './reports/SnapIVReports';
import { BanaKendiniAnlatReportUI } from './reports/BanaKendiniAnlatReports';
import { LgsPusulasiReportUI } from './reports/LgsPusulasiReports';
import { StudentInfoReportUI } from './reports/StudentInfoReports';
import { PsikolojikSaglamlikReportUI } from './reports/psikolojikSaglamlikReports';
import { LiebowitzReportUI } from './reports/liebowitzReports';


const AVAILABLE_TESTS = NEW_FORMS.map(form => ({
  id: form.id, name: form.title, questions: form.items
}));

const PrintStyles = () => (<style>{`
  @media print { 
    @page { size: A4 portrait; margin: 10mm; } 
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background-color: white !important; }
    .no-print { display: none !important; }
    .avoid-break { page-break-inside: avoid !important; break-inside: avoid !important; }
    .bg-slate-900 { background-color: #0f172a !important; color: white !important; }
  }
`}</style>);

const ReportViewer = ({ report, user, onClose, onSaveSubmission, triggerModal }) => {
  const [downloading, setDownloading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [currentAiAnalysis, setCurrentAiAnalysis] = useState(report.data.aiAnalysis || null);

  const isHolland = report.meta.testId === 'holland_inventory';
  const isRiba = report.data.isRiba === true;

  const top3Subjects = isHolland && report.data.chartData 
      ? [...report.data.chartData].sort((a, b) => b.A - a.A).slice(0, 3).map(i => i.subject) 
      : [];

  const isRedAlarm = report.data.summary && report.data.summary.includes("KIRMIZI");
  const theme = isRedAlarm ? { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", hex: "#dc2626", badge: "KIRMIZI ALARM" } : { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", hex: "#059669", badge: "NORMAL SEVİYE" };
  
  const [isEditingAi, setIsEditingAi] = useState(false);
  const [editableAiText, setEditableAiText] = useState("");

  const handleEditClick = () => {
    setEditableAiText(currentAiAnalysis);
    setIsEditingAi(true);
  };

  const handleSaveEdit = () => {
    setCurrentAiAnalysis(editableAiText);
    report.data.aiAnalysis = editableAiText; 
    setIsEditingAi(false);
  };

 const handleRegenerate = () => {
    triggerModal({
      type: 'confirm',
      title: 'Yapay Zeka Analizini Yenile',
      message: 'Mevcut yapay zeka analizi silinecek ve baştan oluşturulacaktır. Bu işlem bir kredinize mal olabilir. Emin misiniz?',
      confirmText: 'Evet, Yeniden Oluştur',
      onConfirm: () => {
        // Modal kendini kapatacak, ardından üretim başlayacak
        setCurrentAiAnalysis(null); 
        handleAiGenerate(); 
      }
    });
  };

 const handleAiGenerate = async () => { 
    setAiLoading(true); 
    try { 
      const rawTestId = report.meta?.testId || report.meta?.formId || report.testId || "";
      const testId = String(rawTestId).toLowerCase().trim();
      const riskLevel = report.data.summary || report.data.totalScore || "Belirtilmemiş";
      
      
      const currentUserId = user?.uid || report.meta?.clinicianUid || "unknown_user";
      
      let qaText = "";
      if (testId.includes('kendini_anlat') || testId.includes('otobiyografi')) {
         const rawAnswers = report.data?.answers || report.meta?.answers || report.answers || {};
         const testDef = AVAILABLE_TESTS.find(t => t.id === testId);
         Object.entries(rawAnswers).forEach(([qId, ansVal]) => {
             const qText = testDef?.questions?.find(q => q.id === parseInt(qId))?.text || `Soru ${qId}`;
             if (ansVal && String(ansVal).trim() !== '') qaText += `SORU: ${qText}\nÖĞRENCİ YANITI: ${ansVal}\n\n`;
         });
      }

      // 1. VARSAYILAN (ESKİ) MOTOR AYARLARI
      let API_URL = "https://us-central1-rehber360-e0808.cloudfunctions.net/analyzeIndividualInventory";
      let payload = { 
        testId: testId,
        riskLevel: riskLevel,
        reportData: report.data, 
        studentClass: report.meta?.studentClass || "Lise",
        qaText: qaText,
        userId: currentUserId // 🛡️ BURADA KİMLİĞİMİZ GİDİYOR
      };

      // --- AKILLI MAKAS (ROUTER) BAŞLIYOR ---
      const CAREER_GROUPS_MAP = {
        'A': "Doğa ve Tarım", 'B': "Mekanik ve Teknoloji", 'C': "Liderlik ve Yönetim",
        'D': "Sanat ve Medya", 'E': "Edebiyat ve İletişim", 'F': "Sosyal Bilimler",
        'G': "Uluslararası İlişkiler", 'H': "Tıp ve Sağlık", 'I': "Finans ve Analiz", 'İ': "Yazılım ve Bilişim"
      };

      // 2. EĞER TEST KARİYER PUSULASI İSE YENİ MOTORA YÖNLENDİR
      if (testId === 'kariyer_pusulasi') {
          API_URL = "https://us-central1-rehber360-e0808.cloudfunctions.net/analyzeKariyerPusulasi";
          
          const scoresObj = report.data.scores || {};
          const sortedKariyer = Object.keys(scoresObj)
              .filter(k => CAREER_GROUPS_MAP[k])
              .map(k => ({ name: CAREER_GROUPS_MAP[k], score: scoresObj[k] }))
              .sort((a,b) => b.score - a.score);
          
          payload = {
              top3Names: sortedKariyer.slice(0,3).map(x => x.name).join(", "),
              scores: scoresObj,
              userId: currentUserId // 🛡️ BURADA KİMLİĞİMİZ GİDİYOR
          };
      } 
      // 3. EĞER TEST MESLEKİ EĞİLİM İSE YENİ MOTORA YÖNLENDİR
      else if (testId === 'mesleki_egilim_belirleme') {
          API_URL = "https://us-central1-rehber360-e0808.cloudfunctions.net/analyzeMeslekiEgilim";
          
          const detailedScores = report.data.detailedScores || [];
          const passedArr = detailedScores
              .filter(s => s.score >= 101)
              .map(s => CAREER_GROUPS_MAP[s.groupKey] || s.groupKey);
          
          payload = {
              passedAreas: passedArr.length > 0 ? passedArr.join(", ") : "Barajı geçen alan yok",
              allScores: detailedScores,
              userId: currentUserId // 🛡️ BURADA KİMLİĞİMİZ GİDİYOR
          };
      }
      // --- AKILLI MAKAS BİTTİ ---

      // SEÇİLEN MOTORA İSTEK AT
      const res = await fetch(API_URL, { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify(payload) 
      }); 
      
      if (!res.ok) { 
        const errData = await res.json(); 
        
        // 🚀 YENİ EKLENEN HATA YAKALAYICI: Eğer kredi yetersizse uyar ve durdur
        if (errData.error === "INSUFFICIENT_CREDITS") {
            throw new Error("⚠️ Bu analiz için krediniz yetersiz! Lütfen Premium pakete geçin veya kredi yükleyin.");
        }
        
        throw new Error(errData.error || "Sunucu hatası."); 
      } 
      
      const data = await res.json(); 
      const generatedText = data.result; 
      
      setCurrentAiAnalysis(generatedText); 
      const updatedReportData = { ...report.data, aiAnalysis: generatedText }; 
      const updatedSubmission = { ...report.meta, reportData: updatedReportData, status: 'reported' }; 
      
      if(onSaveSubmission) await onSaveSubmission(updatedSubmission); 
      
    } catch (error) { 
      console.error("AI Error:", error); 
      triggerModal({
        type: 'alert',
        title: 'Bağlantı Hatası',
        message: error.message
      });
    } finally { 
      setAiLoading(false); 
    } 
  };

  const handlePerfectPrint = () => {
    setDownloading(true);
    
    const content = document.getElementById('printable-report-area').innerHTML;
    const iframe = document.createElement('iframe');
    
    iframe.style.position = 'fixed';
    iframe.style.bottom = '0';
    iframe.style.right = '0';
    iframe.style.width = '1024px';
    iframe.style.height = '100vh';
    iframe.style.opacity = '0.01';
    iframe.style.pointerEvents = 'none';
    iframe.style.zIndex = '-1';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    
    let stylesHTML = '';
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => {
        stylesHTML += el.outerHTML;
    });

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${report.meta?.studentClass} - ${report.meta?.studentNo} Raporu</title>
          ${stylesHTML} <style>
            @media print { 
              @page { size: A4 portrait; margin: 10mm; } 
              body { font-family: 'Segoe UI', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white; } 
              
              /* Giyotin etkisini bitiren native sayfa kesilme önleyiciler */
              .avoid-break, .grid > div, table, tr { 
                page-break-inside: avoid !important; 
                break-inside: avoid !important; 
              }
              h2, h3, h4 { page-break-after: avoid !important; }
              .bg-slate-900 { background-color: #0f172a !important; color: white !important; } 
            }
          </style>
        </head>
        <body class="bg-white text-black">
          ${content}
        </body>
      </html>
    `);
    doc.close();

    const originalTitle = document.title;
    const safeTestName = (report.meta?.testName || 'Rapor').replace(/ /g, '_');
    const fileName = `${report.meta?.studentClass}_${report.meta?.studentNo}_${safeTestName}`;
    document.title = fileName;

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    
    document.title = originalTitle;
    setDownloading(false);
    
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 999000000);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[70] p-4 no-print">
      <PrintStyles />
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-300">
        
        <div className="bg-slate-900 text-white p-4 px-8 flex items-center justify-between shrink-0 shadow-md z-20 no-print">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm"><FileText className="h-6 w-6 text-blue-300"/></div>
                <div><h2 className="font-bold text-lg leading-tight">{report.meta?.studentClass} - {report.meta?.studentNo}</h2><p className="text-slate-400 text-xs">{report.meta?.testName} Raporu</p></div>
            </div>
            <div className="flex gap-3 print:hidden">
                <button 
                  onClick={handlePerfectPrint} 
                  disabled={downloading} 
                  className="px-4 py-2.5 bg-white hover:bg-slate-100 text-slate-800 rounded-xl font-bold text-xs flex items-center transition shadow-sm border border-slate-200"
                >
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                    Yazdır
                </button>
                
                <button 
                  onClick={handlePerfectPrint} 
                  disabled={downloading} 
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs flex items-center transition shadow-lg shadow-blue-900/50"
                >
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Download className="h-4 w-4 mr-2"/>} 
                    PDF Kaydet
                </button>
                
                <button onClick={onClose} className="p-2.5 bg-white/10 rounded-xl hover:bg-white/20 transition text-slate-300 hover:text-white"><X className="h-5 w-5"/></button>
            </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-100 p-8 custom-scrollbar">
            <div id="printable-report-area" className="bg-white max-w-[210mm] mx-auto min-h-[297mm] p-8 sm:p-12 rounded-xl shadow-sm border border-slate-200 relative">
            
                {isHolland ? (
                    <HollandReportUI 
                        report={report} 
                        currentAiAnalysis={currentAiAnalysis} 
                        handleAiGenerate={handleAiGenerate} 
                        aiLoading={aiLoading} 
                    />
                ) : (
                <>
                    {report.meta.testId === 'basarisizlik_nedenleri' || report.meta.testId?.includes('basarisizlik') ? (
                        <BasarisizlikReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'devamsizlik_nedenleri' || report.meta.testId?.includes('devamsizlik') ? (
                        <DevamsizlikReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'sinav_kaygisi' || report.meta.testId?.includes('sinav') ? (
                        <SinavKaygisiReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'mesleki_degerler_envanteri' || report.meta.testId?.includes('degerler') ? (
                        <MeslekiDegerlerReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'internet_bagimliligi' || report.meta.testId?.includes('bagimlilik') ? (
                        <InternetBagimliligiReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'asrs_form' ? (
                        <AsrsReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'kariyer_pusulasi' ? (
                        <KariyerPusulasiReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'mesleki_egilim_belirleme' ? (
                        <MeslekiEgilimReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'rosenberg_benlik_saygisi' ? (
                        <RosenbergReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'dass_21' ? (
                        <Dass21ReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'snap_iv_form' ? (
                        <SnapIVReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'ogrenci_bilgi_formu' || report.meta.testId?.includes('bilgi_formu') ? (
                        <StudentInfoReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'coklu_zeka_envanteri' || report.meta.testId?.includes('coklu_zeka') ? (
                        <CokluZekaReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'bana_kendini_anlat' || report.meta.testId?.includes('kendini_anlat') || report.meta.testId?.includes('otobiyografi') ? (
                        <BanaKendiniAnlatReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'vak_inventory' || report.meta.testId?.includes('vak') ? (
                        <VakReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'lgs_pusulasi' || report.meta.testId?.includes('lgs') ? (
                        <LgsPusulasiReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'kisa_psikolojik_saglamlik' || report.meta.testId?.includes('saglamlik') ? (
                        <PsikolojikSaglamlikReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'beck_anksiyete' || report.meta.testId?.includes('beck') ? (
                        <BeckAnksiyeteReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'liebowitz_sosyal_kaygi' || report.meta.testId?.includes('liebowitz') ? (
                        <LiebowitzReportUI data={report.data} meta={report.meta} />
                    ) : report.meta.testId === 'yasam_pencerem' || report.meta.testId?.includes('yasam') ? (
                        <YasamPenceremReportUI data={report.data} meta={report.meta} />
                    ) : (
                        <>
                            {!currentAiAnalysis && (
                                <div className="mb-6 bg-white border border-slate-200 rounded-lg p-5 avoid-break shadow-sm">
                                    <h3 className="font-bold text-slate-800 text-xs mb-2 flex items-center uppercase tracking-wide">
                                        <Brain className="h-4 w-4 mr-2 text-indigo-500"/> Ön Değerlendirme
                                    </h3>
                                    <p className="text-xs font-medium text-slate-700 leading-relaxed text-justify whitespace-pre-line">
                                        {(!report.data.risks || report.data.risks.length === 0) 
                                            ? "Öğrencinin yanıtları incelendiğinde belirgin bir risk bulgusuna rastlanmamıştır. Genel uyum düzeyinin dengeli olduğu öngörülmekte olup, rutin rehberlik takibi ve sınıf içi gözlem yeterlidir." 
                                            : `Öğrencinin yanıtları incelendiğinde toplam ${report.data.risks.length} adet bulgu/risk tespit edilmiştir. Öğrencinin durumunu derinlemesine analiz etmek, risk alanları arasındaki ilişkiyi görmek ve profesyonel bir müdahale planı oluşturmak için aşağıdaki "Yapay Zeka ile Yorumla" seçeneğini kullanınız.`
                                        }
                                    </p>
                                </div>
                            )}

                            {report.data.risks && report.data.risks.length > 0 && (
                                <div className="mb-6 avoid-break">
                                    <h3 className="font-bold text-slate-800 text-xs mb-3 flex items-center uppercase tracking-wide">
                                        <AlertCircle className="h-4 w-4 mr-2 text-red-500"/> Tespit Edilen Bulgular
                                    </h3>
                                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead className="bg-slate-50 border-b border-slate-200">
                                                <tr>
                                                    <th className="text-left p-2 font-bold text-slate-600">No</th>
                                                    <th className="text-left p-2 font-bold text-slate-600">Öğrencinin İşaretlediği İfade</th>
                                                    <th className="text-center p-2 font-bold text-slate-600">Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {report.data.risks.map((risk, i) => (
                                                    <tr key={i} className="border-b last:border-0 hover:bg-slate-50 transition">
                                                        <td className="p-2 text-slate-400 font-bold w-10">{typeof risk === 'object' ? risk.id : i+1}</td>
                                                        <td className="p-2 text-slate-700 font-medium">{typeof risk === 'object' ? risk.text : risk.replace(" (KRİTİK)","")}</td>
                                                        <td className="p-2 text-center w-20">
                                                            {(typeof risk === 'object' ? risk.isCritical : risk.includes("KRİTİK")) && <span className="bg-red-50 text-red-700 px-2 py-1 rounded-md font-bold text-[9px] border border-red-100 shadow-sm">KRİTİK</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    
{/* YENİ EKLENEN: RİBA BİREYSEL MÜFETTİŞ DÖKÜM TABLOSU */}
                    {isRiba && report.data.userSelections && report.data.userSelections.length > 0 && (
                        <div className="mb-6 avoid-break mt-6">
                            <h3 className="font-bold text-slate-800 text-xs mb-3 flex items-center uppercase tracking-wide">
                                <List className="h-4 w-4 mr-2 text-blue-500"/> Katılımcı Yanıt Dökümü
                            </h3>
                            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="text-left p-2.5 font-bold text-slate-600 w-12 text-center">Soru</th>
                                            <th className="text-left p-2.5 font-bold text-slate-600">İşaretlenen İhtiyaç Alanı (Hedef)</th>
                                            <th className="text-center p-2.5 font-bold text-slate-600 w-24">Seçim</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {report.data.userSelections.map((selection, i) => (
                                            <tr key={i} className="hover:bg-slate-50 transition">
                                                <td className="p-2.5 text-center font-bold text-slate-400">#{selection.id}</td>
                                                <td className="p-2.5 font-medium text-slate-700">{selection.target}</td>
                                                <td className="p-2.5 text-center">
                                                    <span className="bg-blue-50 text-blue-700 font-bold px-2.5 py-1 rounded border border-blue-100 shadow-sm">
                                                        {selection.choice} Şıkkı
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {!currentAiAnalysis && (
                        <div className="mt-4 flex justify-end no-print pt-4 border-t border-slate-100">
                            <button onClick={handleAiGenerate} disabled={aiLoading} className="flex items-center text-xs font-bold text-purple-600 bg-purple-50 hover:bg-purple-100 px-4 py-2 rounded-lg transition-colors border border-purple-200">
                            {aiLoading ? <Loader2 className="animate-spin h-3 w-3 mr-2"/> : <Sparkles className="h-3 w-3 mr-2"/>}
                            {aiLoading ? "Analiz Ediliyor..." : "Yapay Zeka ile Yorumla"}
                            </button>
                        </div>
                    )}
                    
                    {currentAiAnalysis && (
                        <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-6 avoid-break shadow-sm animate-in fade-in zoom-in-95 duration-500 print:bg-white print:border-none print:p-0 print:shadow-none">
                            
                            <div className="flex justify-between items-start md:items-center mb-4 flex-col md:flex-row gap-3">
                                <div>
                                    <h3 className="font-bold text-purple-900 text-sm flex items-center uppercase tracking-wide print:hidden">
                                        <Sparkles className="h-4 w-4 mr-2 text-purple-600"/> Yapay Zeka Destekli Değerlendirme
                                    </h3>
                                    <h3 className="hidden print:block font-bold text-slate-900 text-lg border-b border-slate-300 pb-1 mb-2 w-full">
                                        Psikolojik Danışman Görüşü ve Değerlendirmesi
                                    </h3>
                                    <span className="text-[9px] bg-purple-200 text-purple-800 px-2 py-1 rounded-full font-bold print:hidden">Gemini AI Model</span>
                                </div>

                                {!isEditingAi && (
                                    <div className="flex items-center gap-2 print:hidden bg-white p-1 rounded-lg border border-purple-100 shadow-sm">
                                        <button 
                                            onClick={handleEditClick}
                                            className="flex items-center text-xs font-bold text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors"
                                            title="Metni Düzenle"
                                        >
                                            <Edit3 className="h-3.5 w-3.5 mr-1.5" /> Düzenle
                                        </button>
                                        <div className="w-px h-4 bg-purple-100"></div>
                                        <button 
                                            onClick={handleRegenerate}
                                            disabled={aiLoading}
                                            className="flex items-center text-xs font-bold text-slate-600 hover:text-purple-600 hover:bg-purple-50 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                                            title="Yeni Rapor Oluştur"
                                        >
                                            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${aiLoading ? 'animate-spin' : ''}`} /> Yenile
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="mt-2">
                                {isEditingAi ? (
                                    <div className="animate-in fade-in duration-300 print:hidden">
                                        <textarea
                                            value={editableAiText}
                                            onChange={(e) => setEditableAiText(e.target.value)}
                                            className="w-full min-h-[300px] p-4 text-sm text-slate-700 bg-white border-2 border-purple-300 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all custom-scrollbar leading-relaxed"
                                            placeholder="Yapay zeka analizini buraya yazın veya düzenleyin..."
                                        />
                                        <div className="flex justify-end gap-2 mt-3">
                                            <button 
                                                onClick={() => setIsEditingAi(false)}
                                                className="flex items-center text-xs font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors"
                                            >
                                                <XCircle className="h-4 w-4 mr-1.5" /> İptal
                                            </button>
                                            <button 
                                                onClick={handleSaveEdit}
                                                className="flex items-center text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 shadow-md shadow-purple-200 px-4 py-2 rounded-lg transition-colors"
                                            >
                                                <Save className="h-4 w-4 mr-1.5" /> Kaydet
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="print:text-black">
                                        <AIFormatter text={currentAiAnalysis} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

                    <div className="mt-8 pt-6 border-t-2 border-dashed border-slate-200 print:mt-6 print:pt-4 avoid-break">
                        <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl flex items-start gap-4 print:bg-white print:border-slate-300">
                            <div className="h-10 w-10 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center shrink-0 print:bg-slate-100 print:text-slate-600">
                                <AlertTriangle className="h-5 w-5"/>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 text-xs uppercase tracking-widest mb-1">Önemli Yasal ve Etik Uyarı</h4>
                                <p className="text-[11px] print:text-[10px] text-slate-600 font-medium leading-relaxed text-justify">
                                    Bu rapor bir <strong>tıbbi tanı (teşhis) aracı değildir.</strong> Okul rehberlik servisi tarafından öğrencinin gelişimini desteklemek amacıyla gerçekleştirilen <em>Psikososyal Ön Değerlendirme ve Risk Tarama</em> analizidir. Kesin tıbbi tanı ve tedavi süreçleri için mutlaka bir <strong>Çocuk ve Ergen Ruh Sağlığı ve Hastalıkları Uzmanına (Psikiyatrist)</strong> başvurulmalıdır.
                                </p>
                            </div>
                        </div>
                    </div>
            
            <div className="mt-6 pt-2 border-t border-slate-100 text-[8px] text-slate-300 flex justify-between">
                <span>Rehber360 v23.0 (AI)</span>
                <span>Rapor ID: {report.meta?.id}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;